import { useMemo } from "react"
import { FeatureEnum } from "shared/featureData"
import { Reservation } from "shared/reservation"
import { evaluateReminderDue, reminderDaysToMs } from "shared/reminderSchedule"
import { parseSst2Utc } from "src/sst2DateUtils"
import { useAppSelector } from "src/app/hooks"
import { selectTenantId } from "src/features/me/meSlice"
import useFeatures from "src/features/things/useFeatures"
import { useReminderPeriodDays } from "./useReminderPeriodDays"

const toMs = (v?: string | null) => parseSst2Utc(v)?.getTime()

/**
 * When this reservation's next AUTOMATIC pickup reminder is due, in epoch ms —
 * or `undefined` when the scan will not send one (LOCKER-324).
 *
 * `undefined` whenever the caller must say nothing about automation:
 *  - the tenant has not activated `AutomaticPickupReminder`;
 *  - the tenant is held in dry run — `AutomaticPickupReminderLive` is off, so
 *    the scan counts the reminder and sends nothing;
 *  - the server's `evaluateReminderDue` would skip this reservation for good —
 *    deposited before activation, too old, expired or undatable, picked up, not
 *    awaiting pickup, or out of reminders.
 *
 * It CALLS `evaluateReminderDue` from `shared/` rather than mirroring its guards
 * one at a time: a confirmation quoting a date the scan disagrees with is worse
 * than no confirmation, and every mirrored guard was one more place to drift
 * (review F3.2 — `beforeActivation` had not been mirrored). Only
 * `periodNotElapsed` still yields a date, because that skip is the wait itself.
 */
export function useNextAutomaticReminderAt(
  reservation?: Reservation,
): number | undefined {
  const tenantId = useAppSelector(selectTenantId)
  const { canUseFeature, activatedAtMs } = useFeatures(tenantId)
  const periodDays = useReminderPeriodDays()

  const isActivated = canUseFeature(FeatureEnum.AutomaticPickupReminder)
  const isLive = canUseFeature(FeatureEnum.AutomaticPickupReminderLive)
  const featureActivatedAtMs = activatedAtMs(
    FeatureEnum.AutomaticPickupReminder,
  )
  const createdAt = reservation?.createdAt
  const lastSentMs = reservation?.reminderLastSent
  const nbReminderSent = reservation?.nbReminderSent
  const sst = reservation?.sstReservation

  return useMemo(() => {
    if (!isActivated || !isLive || !createdAt) return undefined

    const { isDue, skipReason, dueAtMs } = evaluateReminderDue({
      nowMs: Date.now(),
      periodMs: reminderDaysToMs(periodDays),
      nbReminderSent,
      lastSentMs,
      createdAtMs: toMs(createdAt),
      sstStatus: sst?.status,
      deliveryTime: sst?.delivery_time,
      receptionTime: sst?.reception_time,
      expirationMs: toMs(sst?.expiration_time),
      depositedAtMs: toMs(sst?.delivery_time),
      featureActivatedAtMs,
    })
    return isDue || skipReason === "periodNotElapsed" ? dueAtMs : undefined
  }, [
    isActivated,
    isLive,
    featureActivatedAtMs,
    createdAt,
    lastSentMs,
    nbReminderSent,
    sst?.status,
    sst?.delivery_time,
    sst?.reception_time,
    sst?.expiration_time,
    periodDays,
  ])
}

export default useNextAutomaticReminderAt
